import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Store, Loader2, Check, Save, Info, Zap, Lock } from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Tela onde o afiliado marca em quais marketplaces quer vender (modo
 * manual, todos têm) e, se já tiver o selo de automação 24/7 liberado
 * pelo admin, escolhe quais desses deixar rodando automático.
 */
const MeusMarketplaces = ({ usuarioId }) => {
  const [marketplacesDisponiveis, setMarketplacesDisponiveis] = useState([]);
  const [selecionados, setSelecionados] = useState([]);
  const [automaticos, setAutomaticos] = useState([]);
  const [temAutomacaoLiberada, setTemAutomacaoLiberada] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [salvandoAutomaticos, setSalvandoAutomaticos] = useState(false);

  const carregar = async () => {
    setCarregando(true);
    try {
      const [respDisponiveis, respDesejados, respAutomacao, respAutomaticos] = await Promise.all([
        authService.fetchAuth(`${BACKEND_URL}/api/oauth/marketplaces`),
        authService.fetchAuth(`${BACKEND_URL}/api/perfil/marketplaces-desejados/${usuarioId}`),
        authService.fetchAuth(`${BACKEND_URL}/api/perfil/automacao-24-7/${usuarioId}`),
        authService.fetchAuth(`${BACKEND_URL}/api/perfil/marketplaces-automaticos/${usuarioId}`),
      ]);
      if (respDisponiveis.ok) { const dataDisp = await respDisponiveis.json(); setMarketplacesDisponiveis(Array.isArray(dataDisp) ? dataDisp : (dataDisp.marketplaces || [])); }
      if (respDesejados.ok) {
        const data = await respDesejados.json();
        setSelecionados(data.marketplaces_desejados || []);
      }
      if (respAutomacao.ok) {
        const data = await respAutomacao.json();
        setTemAutomacaoLiberada(data.automacao_24_7_liberada);
      }
      if (respAutomaticos.ok) {
        const data = await respAutomaticos.json();
        setAutomaticos(data.marketplaces_automaticos || []);
      }
    } catch (error) {
      console.error("Erro ao carregar marketplaces:", error);
      toast.error("Não foi possível carregar os marketplaces.");
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (usuarioId) carregar();
    // eslint-disable-next-line
  }, [usuarioId]);

  const alternar = (id) => {
    setSelecionados((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
    // Se desmarcou um marketplace do modo manual, remove ele do automático também
    setAutomaticos((prev) => prev.filter((m) => m !== id));
  };

  const alternarAutomatico = (id) => {
    setAutomaticos((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      const resp = await authService.fetchAuth(
        `${BACKEND_URL}/api/perfil/marketplaces-desejados/${usuarioId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ marketplaces: selecionados }),
        }
      );
      if (!resp.ok) throw new Error("Erro ao salvar");
      toast.success("Marketplaces atualizados! Agora conecte cada um na extensão ClickHub Connect.");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvando(false);
    }
  };

  const salvarAutomaticos = async () => {
    setSalvandoAutomaticos(true);
    try {
      const resp = await authService.fetchAuth(
        `${BACKEND_URL}/api/perfil/marketplaces-automaticos/${usuarioId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ marketplaces: automaticos }),
        }
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao salvar");
      toast.success("Configuração do modo automático salva!");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvandoAutomaticos(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <Store className="w-7 h-7 text-orange-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">Onde Você Quer Vender</h1>
          <p className="text-sm text-gray-400">Marque os marketplaces em que quer publicar seus produtos</p>
        </div>
      </div>

      <Card className="p-3 bg-blue-950/30 border-blue-800 mb-6 flex items-start gap-2">
        <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
        <p className="text-xs text-blue-300">
          Depois de marcar aqui, você precisa autorizar a conexão de cada loja na extensão ClickHub Connect,
          direto no navegador — sua conta em cada marketplace é sua e só você pode autorizar o acesso.
        </p>
      </Card>

      {carregando ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Carregando...
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            {marketplacesDisponiveis.map((mp) => {
              const marcado = selecionados.includes(mp.id);
              return (
                <button
                  key={mp.id}
                  onClick={() => alternar(mp.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    marcado
                      ? "border-orange-500 bg-orange-500/10"
                      : "border-gray-700 bg-gray-800/50 hover:border-gray-600"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-white font-medium text-sm">{mp.name}</p>
                    {marcado && (
                      <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between pb-6 mb-6 border-b border-gray-800">
            <p className="text-sm text-gray-400">{selecionados.length} marketplace(s) selecionado(s)</p>
            <Button onClick={salvar} disabled={salvando} className="bg-orange-600 hover:bg-orange-500">
              {salvando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar Seleção
            </Button>
          </div>

          {/* Seção do modo automático 24/7 - condicionada ao selo */}
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-5 h-5 text-orange-400" />
            <h2 className="text-lg font-bold text-white">Modo Automático 24/7</h2>
          </div>

          {!temAutomacaoLiberada ? (
            <Card className="p-5 bg-gray-800/50 border-gray-700 flex items-start gap-3">
              <Lock className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-white text-sm font-medium">Modo automático ainda não liberado</p>
                <p className="text-xs text-gray-400 mt-1 mb-3">
                  Você está vendendo no modo manual. Para vender 24 horas por dia, 7 dias por semana, com o robô
                  cuidando de tudo automaticamente, fale com nosso suporte.
                </p>
                <a
                  href={`https://wa.me/5521964894355?text=${encodeURIComponent("Olá! Sou afiliado ClickHub e quero autorizar o modo automático 24/7 na minha conta.")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium px-3 py-2 rounded-lg"
                >
                  Falar com Suporte no WhatsApp
                </a>
              </div>
            </Card>
          ) : (
            <>
              <Card className="p-3 bg-emerald-950/30 border-emerald-800 mb-4">
                <p className="text-xs text-emerald-400">
                  ✓ Modo automático liberado! Escolha abaixo, dentre os marketplaces já selecionados acima,
                  quais quer deixar rodando 24/7.
                </p>
              </Card>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                {marketplacesDisponiveis
                  .filter((mp) => selecionados.includes(mp.id))
                  .map((mp) => {
                    const marcadoAuto = automaticos.includes(mp.id);
                    return (
                      <button
                        key={mp.id}
                        onClick={() => alternarAutomatico(mp.id)}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          marcadoAuto
                            ? "border-emerald-500 bg-emerald-500/10"
                            : "border-gray-700 bg-gray-800/50 hover:border-gray-600"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-white font-medium text-sm">{mp.name}</p>
                          {marcadoAuto && (
                            <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                              <Zap className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-400">{automaticos.length} no modo automático</p>
                <Button onClick={salvarAutomaticos} disabled={salvandoAutomaticos} className="bg-emerald-600 hover:bg-emerald-500">
                  {salvandoAutomaticos ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                  Salvar Automáticos
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default MeusMarketplaces;
